import React, { useMemo } from 'react';
import {
  Box,
  Flex,
  Stack,
  VStack,
  HStack,
  Text,
  Image,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  BracketMatchDisplay,
  MundialTeam,
} from '../../types/mundial';
import { KNOCKOUT_ROUNDS } from '../../constants/mundialBracket';
import {
  BRACKET_LEFT_GROUPS,
  BRACKET_LEFT_QF,
  BRACKET_LEFT_R16,
  BRACKET_LEFT_R32,
  BRACKET_LEFT_SF,
  BRACKET_RIGHT_GROUPS,
  BRACKET_RIGHT_QF,
  BRACKET_RIGHT_R16,
  BRACKET_RIGHT_R32,
  BRACKET_RIGHT_SF,
  GROUP_BRACKET_COLORS,
  PENDING_TEAM_LABEL,
} from '../../constants/mundialBracket';

const FIFA_WC_2026_LOGO = '/images/fifa-world-cup-2026-logo.png';
const MATCH_BOX_W = '132px';
const GROUP_PANEL_W = '108px';
const BRACKET_MIN_WIDTH = '1680px';

interface WorldCupBracketViewProps {
  teams: MundialTeam[];
  matchesByRound: Record<string, BracketMatchDisplay[]>;
}

function buildSlotMap(
  matchesByRound: Record<string, BracketMatchDisplay[]>
): Map<string, BracketMatchDisplay> {
  const map = new Map<string, BracketMatchDisplay>();
  Object.values(matchesByRound).flat().forEach((m) => {
    if (m.bracketSlot) map.set(m.bracketSlot, m);
  });
  return map;
}

interface BracketTeamRowProps {
  name: string;
  flagUrl?: string;
  code?: string;
  slotLabel?: string;
  score?: number | null;
  isWinner?: boolean;
}

const BracketTeamRow: React.FC<BracketTeamRowProps> = ({
  name,
  flagUrl,
  code,
  slotLabel,
  score,
  isWinner,
}) => {
  const bg = useColorModeValue('gray.700', 'gray.800');
  const winnerBg = useColorModeValue('pitch.600', 'pitch.500');
  const textColor = 'white';
  const muted = 'whiteAlpha.700';
  const pending = name === PENDING_TEAM_LABEL || !name;

  const display = pending && slotLabel ? slotLabel : name;

  return (
    <HStack
      px={2}
      py={1}
      bg={isWinner ? winnerBg : bg}
      borderRadius="md"
      justify="space-between"
      minH="28px"
      spacing={2}
    >
      <HStack spacing={1.5} minW={0} flex={1}>
        {flagUrl && !pending ? (
          <Image src={flagUrl} alt="" boxSize="18px" borderRadius="sm" flexShrink={0} />
        ) : (
          <Box boxSize="18px" bg="whiteAlpha.200" borderRadius="sm" flexShrink={0} />
        )}
        <Text
          fontSize="xs"
          fontWeight={isWinner ? 'bold' : 'medium'}
          color={textColor}
          noOfLines={1}
        >
          {display}
        </Text>
      </HStack>
      {score !== null && score !== undefined && (
        <Text fontSize="xs" fontWeight="bold" color={textColor}>
          {score}
        </Text>
      )}
      {code && !pending && (
        <Text fontSize="10px" color={muted}>
          {code}
        </Text>
      )}
    </HStack>
  );
};

interface BracketMatchBoxProps {
  match?: BracketMatchDisplay;
}

const BracketMatchBox: React.FC<BracketMatchBoxProps> = ({ match }) => {
  const borderColor = useColorModeValue('whiteAlpha.300', 'whiteAlpha.300');
  const slotColor = useColorModeValue('whiteAlpha.700', 'whiteAlpha.700');
  const hasResult = match && match.scoreA !== null && match.scoreB !== null;

  if (!match) {
    return (
      <Box
        w={MATCH_BOX_W}
        flexShrink={0}
        p={1.5}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="lg"
        bg="blackAlpha.400"
      >
        <VStack spacing={1}>
          <Box h="28px" w="full" bg="whiteAlpha.100" borderRadius="md" />
          <Box h="28px" w="full" bg="whiteAlpha.100" borderRadius="md" />
        </VStack>
      </Box>
    );
  }

  const teamAWins = hasResult && match.winnerCode === match.teamA.code;
  const teamBWins = hasResult && match.winnerCode === match.teamB.code;

  return (
    <Box
      w={MATCH_BOX_W}
      flexShrink={0}
      p={1.5}
      borderWidth="1px"
      borderColor={hasResult ? 'pitch.400' : borderColor}
      borderRadius="lg"
      bg="blackAlpha.500"
    >
      {match.bracketSlot && (
        <Text fontSize="10px" color={slotColor} textAlign="center" mb={1}>
          {match.bracketSlot}
        </Text>
      )}
      <VStack spacing={1}>
        <BracketTeamRow
          name={match.teamA.name}
          flagUrl={match.teamA.flagUrl}
          code={match.teamA.code}
          slotLabel={match.sourceLabelA}
          score={hasResult ? match.scoreA : undefined}
          isWinner={!!teamAWins}
        />
        <BracketTeamRow
          name={match.teamB.name}
          flagUrl={match.teamB.flagUrl}
          code={match.teamB.code}
          slotLabel={match.sourceLabelB}
          score={hasResult ? match.scoreB : undefined}
          isWinner={!!teamBWins}
        />
      </VStack>
    </Box>
  );
};

interface GroupPanelProps {
  groupId: string;
  teams: MundialTeam[];
}

const GroupPanel: React.FC<GroupPanelProps> = ({ groupId, teams }) => {
  const color = GROUP_BRACKET_COLORS[groupId] || '#64748b';
  const groupTeams = teams.filter((t) => t.group.toUpperCase() === groupId);

  return (
    <Box
      w={GROUP_PANEL_W}
      flexShrink={0}
      borderWidth="2px"
      borderColor={color}
      borderRadius="lg"
      overflow="hidden"
      bg="blackAlpha.600"
    >
      <Box bg={color} py={1} px={2}>
        <Text fontSize="xs" fontWeight="bold" color="white" textAlign="center">
          GRUPO {groupId}
        </Text>
      </Box>
      <VStack spacing={0} p={1.5} align="stretch">
        {groupTeams.slice(0, 4).map((team) => (
          <HStack key={team.code} spacing={1.5} py={0.5}>
            <Image src={team.flag_url} alt="" boxSize="16px" borderRadius="sm" />
            <Text fontSize="10px" color="white" noOfLines={1}>
              {team.code}
            </Text>
          </HStack>
        ))}
        {groupTeams.length === 0 &&
          Array.from({ length: 4 }).map((_, i) => (
            <Box key={i} h="18px" bg="whiteAlpha.100" borderRadius="sm" my={0.5} />
          ))}
      </VStack>
    </Box>
  );
};

interface BracketColumnProps {
  slots: string[];
  slotMap: Map<string, BracketMatchDisplay>;
  gap: number;
}

const BracketColumn: React.FC<BracketColumnProps> = ({ slots, slotMap, gap }) => (
  <VStack spacing={gap} justify="center" py={2} flexShrink={0}>
    {slots.map((slot) => (
      <BracketMatchBox key={slot} match={slotMap.get(slot)} />
    ))}
  </VStack>
);

interface MobileBracketListProps {
  matchesByRound: Record<string, BracketMatchDisplay[]>;
}

const MobileBracketList: React.FC<MobileBracketListProps> = ({ matchesByRound }) => {
  const cardBg = useColorModeValue('white', 'slate.850');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const roundLabel = useColorModeValue('pitch.700', 'pitch.200');

  return (
    <VStack spacing={4} align="stretch">
      {KNOCKOUT_ROUNDS.map(({ matchType, label }) => {
        const roundMatches = matchesByRound[matchType] ?? [];
        if (roundMatches.length === 0) return null;

        return (
          <Box key={matchType}>
            <Text fontSize="sm" fontWeight="bold" color={roundLabel} mb={2}>
              {label}
            </Text>
            <VStack spacing={2} align="stretch">
              {roundMatches.map((match) => {
                const hasResult = match.scoreA !== null && match.scoreB !== null;
                const scoreLabel = hasResult ? `${match.scoreA} - ${match.scoreB}` : 'Por definir';

                return (
                  <Box
                    key={match.id}
                    p={3}
                    borderWidth="1px"
                    borderColor={borderColor}
                    borderRadius="lg"
                    bg={cardBg}
                  >
                    <Stack
                      direction={{ base: 'column', sm: 'row' }}
                      spacing={2}
                      align={{ base: 'stretch', sm: 'center' }}
                      justify="space-between"
                    >
                      <HStack flex={1} minW={0} spacing={2}>
                        <Image
                          src={match.teamA.flagUrl || '/images/mundial_2026.png'}
                          alt=""
                          boxSize="24px"
                          flexShrink={0}
                        />
                        <Text fontSize="sm" fontWeight="semibold" noOfLines={2}>
                          {match.teamA.name}
                        </Text>
                      </HStack>
                      <Text
                        fontWeight="bold"
                        fontSize="sm"
                        flexShrink={0}
                        px={2}
                        alignSelf={{ base: 'center', sm: 'auto' }}
                      >
                        {scoreLabel}
                      </Text>
                      <HStack flex={1} minW={0} justify={{ base: 'flex-start', sm: 'flex-end' }} spacing={2}>
                        <Text fontSize="sm" fontWeight="semibold" noOfLines={2} textAlign={{ base: 'left', sm: 'right' }}>
                          {match.teamB.name}
                        </Text>
                        <Image
                          src={match.teamB.flagUrl || '/images/mundial_2026.png'}
                          alt=""
                          boxSize="24px"
                          flexShrink={0}
                        />
                      </HStack>
                    </Stack>
                  </Box>
                );
              })}
            </VStack>
          </Box>
        );
      })}
    </VStack>
  );
};

export const WorldCupBracketView: React.FC<WorldCupBracketViewProps> = ({
  teams,
  matchesByRound,
}) => {
  const slotMap = useMemo(() => buildSlotMap(matchesByRound), [matchesByRound]);
  const finalMatch = slotMap.get('FINAL');
  const bg = useColorModeValue('slate.900', 'black');
  const trophyColor = useColorModeValue('gold.400', 'gold.300');

  return (
    <>
      <Box display={{ base: 'block', lg: 'none' }}>
        <MobileBracketList matchesByRound={matchesByRound} />
      </Box>

      <Box
        display={{ base: 'none', lg: 'block' }}
        w="100%"
        overflowX="auto"
        overflowY="hidden"
        borderRadius="xl"
        bg={bg}
        p={{ base: 3, md: 5 }}
        borderWidth="1px"
        borderColor="whiteAlpha.200"
        sx={{
          WebkitOverflowScrolling: 'touch',
          '&::-webkit-scrollbar': { height: '8px' },
          '&::-webkit-scrollbar-thumb': {
            bg: 'whiteAlpha.300',
            borderRadius: 'full',
          },
        }}
      >
      <Flex
        minW={BRACKET_MIN_WIDTH}
        w="max-content"
        mx="auto"
        align="center"
        justify="center"
        gap={{ base: 2, md: 3, lg: 4 }}
        py={2}
      >
        {/* Grupos izquierda */}
        <VStack spacing={1.5} flexShrink={0}>
          {BRACKET_LEFT_GROUPS.map((g) => (
            <GroupPanel key={g} groupId={g} teams={teams} />
          ))}
        </VStack>

        {/* Llave izquierda */}
        <BracketColumn slots={BRACKET_LEFT_R32} slotMap={slotMap} gap={3} />
        <BracketColumn slots={BRACKET_LEFT_R16} slotMap={slotMap} gap={8} />
        <BracketColumn slots={BRACKET_LEFT_QF} slotMap={slotMap} gap={20} />
        <BracketColumn slots={BRACKET_LEFT_SF} slotMap={slotMap} gap={0} />

        {/* Centro — Final */}
        <VStack spacing={2} px={2} minW="170px" flexShrink={0} align="center">
          <Text
            fontSize="xs"
            fontWeight="bold"
            letterSpacing="wider"
            color="whiteAlpha.800"
            textAlign="center"
          >
            CAMPEÓN MUNDIAL
          </Text>
          <Image
            src={FIFA_WC_2026_LOGO}
            alt="FIFA World Cup 2026"
            maxH={{ base: '90px', md: '128px' }}
            w="auto"
            objectFit="contain"
            mx="auto"
            draggable={false}
          />
          <Box transform="scale(1.05)">
            <BracketMatchBox match={finalMatch} />
          </Box>
          {finalMatch?.winnerCode && (
            <Text fontSize="sm" fontWeight="bold" color={trophyColor} textAlign="center" px={1}>
              {finalMatch.teamA.code === finalMatch.winnerCode
                ? finalMatch.teamA.name
                : finalMatch.teamB.name}
            </Text>
          )}
          <Text fontSize="xs" color="whiteAlpha.600" mt={2}>
            TERCERO MUNDIAL
          </Text>
        </VStack>

        {/* Llave derecha */}
        <BracketColumn slots={BRACKET_RIGHT_SF} slotMap={slotMap} gap={0} />
        <BracketColumn slots={BRACKET_RIGHT_QF} slotMap={slotMap} gap={20} />
        <BracketColumn slots={BRACKET_RIGHT_R16} slotMap={slotMap} gap={8} />
        <BracketColumn slots={BRACKET_RIGHT_R32} slotMap={slotMap} gap={3} />

        {/* Grupos derecha */}
        <VStack spacing={1.5} flexShrink={0}>
          {BRACKET_RIGHT_GROUPS.map((g) => (
            <GroupPanel key={g} groupId={g} teams={teams} />
          ))}
        </VStack>
      </Flex>
      </Box>
    </>
  );
};
